import { DestroyRef, inject, Injectable } from '@angular/core';
import { CACHE_NAME, CACHE_VERSION, METADATA_CACHE_NAME } from '../config/cache.config';
import type { CachedResponse, CacheMetadata } from '../models/cache.models';
import { createLogger } from '@hunting-nature/shared-core/logging';

/**
 * CacheService
 *
 * Manages API response caching via the browser Cache API with a separate
 * metadata bucket for TTL, maxAge, and serve-count tracking.
 *
 * Automatic cleanup:
 *   - On app startup: expired entries are purged immediately.
 *   - On `visibilitychange → hidden` (tab switch / Capacitor pause):
 *     another purge runs while the user is away.
 *
 * Uses browser Cache API for storage with metadata in a separate cache.
 */
@Injectable({
  providedIn: 'root',
})
export class CacheService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly log = createLogger('CacheService');
  private readonly CACHE_NAME = CACHE_NAME;
  private readonly METADATA_CACHE_NAME = METADATA_CACHE_NAME;
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly CACHE_VERSION = CACHE_VERSION;

  constructor() {
    // Purge again whenever the tab/app goes to background — the user
    // isn't interacting, so there is zero perceived impact.
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    });
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState !== 'hidden') return;

    this.clearExpired().catch(error => {
      this.log.error('Failed to clear expired cache in background', { error });
    });
  };

  private deleteInBackground(cacheKey: Request): void {
    this.delete(cacheKey).catch(error => {
      this.log.error('Failed to delete expired cache entry', { error });
    });
  }

  /**
   * Store a response in cache with metadata
   */
  async set(cacheKey: Request, response: Response, config: { freshForMs?: number; maxAgeMs?: number } = {}): Promise<boolean> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const metadataCache = await caches.open(this.METADATA_CACHE_NAME);

      // Clone response to avoid consuming it
      const responseClone = response.clone();

      // Create metadata
      const metadata: CacheMetadata = {
        cachedAt: new Date().toISOString(),
        fetchedAt: new Date().toISOString(),
        source: 'network',
        version: this.CACHE_VERSION,
        ttl: config.freshForMs ?? this.DEFAULT_TTL,
        maxAgeMs: config.maxAgeMs ?? 0,
        isExpired: false,
        url: cacheKey.url,
        method: cacheKey.method,
        servedCount: 0,
      };

      // Store the actual response
      await cache.put(cacheKey, responseClone);

      // Store metadata separately
      const metadataResponse = Response.json(metadata);
      await metadataCache.put(cacheKey, metadataResponse);

      return true;
    } catch (error) {
      this.log.error('Failed to store in cache', { error });
      return false;
    }
  }

  /**
   * Get a cached response with metadata.
   *
   * Enforces two age checks:
   *   1. `ttl` (freshForMs) — marks the entry as stale but still servable.
   *   2. `maxAgeMs` — hard cap; entries older than this are deleted and never served.
   */
  async get(cacheKey: Request): Promise<CachedResponse | null> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const metadataCache = await caches.open(this.METADATA_CACHE_NAME);

      const [cachedResponse, metadataResponse] = await Promise.all([cache.match(cacheKey), metadataCache.match(cacheKey)]);

      if (!cachedResponse || !metadataResponse) {
        return null;
      }

      // Parse metadata
      const metadata: CacheMetadata = await metadataResponse.json();

      const now = Date.now();
      const cachedTime = new Date(metadata.cachedAt).getTime();
      const ageMs = now - cachedTime;

      // Hard cap: if maxAgeMs is set and exceeded, delete and refuse to serve.
      if (metadata.maxAgeMs && metadata.maxAgeMs > 0 && ageMs > metadata.maxAgeMs) {
        this.log.debug('Entry exceeded maxAge, deleting', {
          ageSeconds: Math.round(ageMs / 1000),
          maxAgeSeconds: Math.round(metadata.maxAgeMs / 1000),
          url: cacheKey.url,
        });
        this.deleteInBackground(cacheKey);
        return null;
      }

      // Soft expiry: mark as stale but still servable.
      const isExpired = metadata.ttl > 0 && ageMs > metadata.ttl;
      metadata.isExpired = isExpired;
      metadata.servedCount = (metadata.servedCount ?? 0) + 1;

      // Update served count
      await this.updateMetadata(cacheKey, metadata);

      // Parse response
      const body = await cachedResponse.json();
      const headers: Record<string, string> = {};
      cachedResponse.headers.forEach((value, key) => {
        headers[key] = value;
      });

      return {
        body,
        status: cachedResponse.status,
        statusText: cachedResponse.statusText,
        headers,
        metadata,
      };
    } catch (error) {
      this.log.error('Failed to retrieve from cache', { error });
      return null;
    }
  }

  /**
   * Update metadata for a cache entry
   */
  private async updateMetadata(cacheKey: Request, metadata: CacheMetadata): Promise<void> {
    try {
      const metadataCache = await caches.open(this.METADATA_CACHE_NAME);
      const metadataResponse = Response.json(metadata);
      await metadataCache.put(cacheKey, metadataResponse);
    } catch (error) {
      this.log.error('Failed to update metadata', { error });
    }
  }

  /**
   * Delete a specific cache entry (response + metadata).
   */
  async delete(cacheKey: Request): Promise<boolean> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const metadataCache = await caches.open(this.METADATA_CACHE_NAME);

      await Promise.all([cache.delete(cacheKey), metadataCache.delete(cacheKey)]);

      return true;
    } catch (error) {
      this.log.error('Failed to delete from cache', { error });
      return false;
    }
  }

  /**
   * Delete entries that exceed their `maxAgeMs` hard cap.
   *
   * Only enforces the hard upper-age limit — entries that are merely
   * past their `ttl` (soft expiry) are kept so they can still serve
   * as offline fallbacks. The interceptor marks them as stale and
   * triggers a background refresh when the network is available.
   *
   * Called automatically on startup and when the tab goes to background.
   */
  async clearExpired(): Promise<number> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const metadataCache = await caches.open(this.METADATA_CACHE_NAME);

      const requests = await cache.keys();
      let deletedCount = 0;

      for (const request of requests) {
        const metadataResponse = await metadataCache.match(request);
        if (!metadataResponse) continue;

        const metadata: CacheMetadata = await metadataResponse.json();
        const ageMs = Date.now() - new Date(metadata.cachedAt).getTime();

        if (metadata.maxAgeMs && metadata.maxAgeMs > 0 && ageMs > metadata.maxAgeMs) {
          await this.delete(request);
          deletedCount++;
        }
      }

      if (deletedCount > 0) this.log.debug('Cleared entries past maxAge', { deletedCount });
      return deletedCount;
    } catch (error) {
      this.log.error('Failed to clear expired cache', { error });
      return 0;
    }
  }
}
